import React from 'react'

interface ClientDashboardV3ScoreGaugeProps {
  className?: string
  score?: number // 0-100
}

/**
 * Dynamic SVG Score Gauge (semi-circle)
 * Needle correctly maps 0 → left (Poor) to 100 → right (Excellent)
 */
export default function ClientDashboardV3ScoreGauge({
  className = 'h-auto w-full',
  score = 50,
}: ClientDashboardV3ScoreGaugeProps) {
  const clampedScore = Math.max(0, Math.min(100, score ?? 0))

  // Semi-circle: -90° (Poor / left) → +90° (Excellent / right). The five coloured
  // segments are equal fifths but the score bands are not (Poor 0–39, Fair 40–54,
  // Average 55–69, Good 70–84, Excellent 85–100), so map piecewise: each band fills its
  // own segment and the needle always points at the band shown in the score pill.
  const bandStarts = [0, 40, 55, 70, 85, 100]
  const band = bandStarts.findIndex((_, i) => i === 4 || clampedScore < bandStarts[i + 1])
  const within = (clampedScore - bandStarts[band]) / (bandStarts[band + 1] - bandStarts[band])
  const angle = -90 + ((band + within) / 5) * 180

  return (
    <svg
      viewBox="0 0 200 115"
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Performance score gauge"
    >
      {/* Colored segments — palette matches the v2 Health Score mockups */}
      <path
        d="M0 98.6185C0.281566 78.4543 6.65266 58.8463 18.2772 42.3677L50.5855 65.1584C43.5567 75.1222 39.7044 86.9782 39.5342 99.1705L0 98.6185Z"
        fill="#F04438"
      />
      <path
        d="M19.9194 40.0996C31.9996 23.952 48.6793 11.8336 67.7698 5.33472L80.5116 42.7629C68.9685 46.6924 58.883 54.0199 51.5788 63.7836L19.9194 40.0996Z"
        fill="#F79009"
      />
      <path
        d="M70.4253 4.47247C89.6898 -1.49082 110.307 -1.49082 129.572 4.47247L117.88 42.2418C106.232 38.6361 93.7653 38.6361 82.117 42.2418L70.4253 4.47247Z"
        fill="#EAAA08"
      />
      <path
        d="M132.225 5.33472C151.315 11.8336 167.995 23.952 180.075 40.0996L148.416 63.7836C141.111 54.0199 131.026 46.6924 119.483 42.7629L132.225 5.33472Z"
        fill="#85CB33"
      />
      <path
        d="M181.723 42.3677C193.347 58.8463 199.718 78.4543 200 98.6185L160.466 99.1705C160.295 86.9782 156.443 75.1222 149.414 65.1584L181.723 42.3677Z"
        fill="#17B26A"
      />

      {/* Labels */}
      <text
        transform="matrix(0.30902 -0.951055 0.951058 0.309014 11.9673 88.5431)"
        fill="white"
        fontFamily="Inter"
        fontSize="9"
        fontWeight="bold"
      >
        <tspan x="1.5" y="9.6">Poor</tspan>
      </text>
      <text
        transform="matrix(0.80902 -0.587781 0.58779 0.809014 41.3525 37.3339)"
        fill="white"
        fontFamily="Inter"
        fontSize="9"
        fontWeight="bold"
      >
        <tspan x="1.5" y="9.6">Fair</tspan>
      </text>
      <text fill="white" fontFamily="Inter" fontSize="9" fontWeight="bold">
        <tspan x="78.27" y="23.56">Average</tspan>
      </text>
      <text
        transform="matrix(0.80902 0.587781 -0.58779 0.809014 139.946 19.3618)"
        fill="white"
        fontFamily="Inter"
        fontSize="9"
        fontWeight="bold"
      >
        <tspan x="2" y="9.6">Good</tspan>
      </text>
      <text
        transform="matrix(0.30902 0.951055 -0.951058 0.309014 175.321 47.993)"
        fill="white"
        fontFamily="Inter"
        fontSize="9"
        fontWeight="bold"
      >
        <tspan x="3.26" y="9.6">Excellent</tspan>
      </text>

      {/* Dynamic Needle – rotates around the true pivot */}
      <g transform={`rotate(${angle} 100 100)`}>
        {/* Tapered needle pointing straight up when angle = 0 */}
        <path
          d="M95.5 100 L100 42 L104.5 100 Z"
          fill="#101828"
        />
        {/* Pivot */}
        <circle cx="100" cy="100" r="7.5" fill="#101828" />
        <circle cx="100" cy="100" r="4" fill="#F8FAFC" />
      </g>
    </svg>
  )
}